/**
 * Docked at a berth.
 *
 * Where the game starts and where it keeps returning: you are on a pad, your
 * ship is in front of you, and everything a station offers is one click away
 * across the top. Flight is what happens between visits here.
 *
 * The view is the *live system*, not a diorama of a deck. Same scene the system
 * view builds — star, planets, moons, orbits, the lot — with the camera parked
 * on one berth of one station. So the station is genuinely in orbit and the
 * system genuinely turns around you while you are sitting in it, which is most
 * of what makes a station feel like a place rather than a menu.
 *
 * The berth is generated, not dressed: it is the pad the packer actually put
 * there, on the deck it actually belongs to.
 *
 * ── What is deliberately missing ────────────────────────────────────────────
 *
 * Missions, trading and reputation are all real systems that do not exist yet,
 * and the panels say so rather than showing invented listings. A mocked-up
 * trade screen would be indistinguishable from a working one at a glance, and
 * the first thing it would do is teach the wrong prices.
 */

import {
  CONTROL_POST,
  FACTIONS,
  PAD_LABELS,
  Rng,
  SHIP_DEPTH,
  beatFor,
  flightTime,
  getStationGeometry,
  getSystem,
  isAction,
  cargoWeight,
  crewLine,
  sellPrice,
  sellSome,
  traderStock,
  formatLinelits,
  crewmember,
  itemWeight,
  makeOfficial,
  type Crewmember,
  type StaffRole,
  type CargoItem,
  type TutorialStep,
  type Wallet,
  type FactionId,
  type ShipGraph,
  type SystemId,
  STATION_PANELS,
  type StationPanel,
} from '@interspacial/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { GAME_SCALE, buildSystemScene, padSurface } from './systemScene';
import { buildShipMesh } from './shipMesh';
import { ReputationBoard } from './ReputationBoard';
import { StationShop } from './StationShop';
import { FlightRadio, type RadioChannel, type RadioMessage } from './FlightRadio';
import { peerClock, type ChatLine, type PeerFeed } from '../net/peers';
import { createRemoteShips, sceneLookups } from './remoteShips';
import { mountViewport } from './viewport';
import { RadioAlert, type Alert } from './RadioAlert';
import { FabricatorPanel } from './FabricatorPanel';
import { useRadio } from './useRadio';
import { MissionBoard } from './MissionBoard';
import { stationTheme, type StationTheme } from './stationTheme';
import { factionIconUrl } from './factionIcons';
import { createSpaceBackground } from './spaceBackground';
import { createSystemSky } from './systemSky';
import { createStructureMaterial, withEdges } from './shaders/structure';
import { HUD_SCALE, TOUCH_UI, hudScaled } from './hudScale';

const FOV = 55;

/**
 * How wide one of the station's counters wants to be.
 *
 * The panel is this times however many are open, capped by the room on screen —
 * so a berth with one counter is one counter wide rather than one counter
 * stretched across the whole view. Sized from the widest thing a column holds:
 * the trader's vault controls ask for 300 and the section adds its own padding.
 */
const COLUMN = 320;

/**
 * How tall the counters stand, as a percentage of the viewport, before the
 * per-device multipliers at the point of use.
 *
 * A third was the original judgement and the reasoning behind it still holds:
 * full height buries the berth you are standing in behind a wall of shop, and
 * the ship staying visible is the point of being docked rather than in a menu.
 */
const BERTH_HEIGHT = 33;

/** The station panels, in the order they sit across the top. */
/**
 * The six things a station does.
 *
 * Not a tab list any more — they are all on screen at once — but the set is
 * still worth naming in one place, and `describe` is keyed by it.
 */
type TabId = 'missions' | 'trader' | 'reputation' | 'cargo' | 'mechanic' | 'chat';

export interface DockedViewProps {
  /**
   * What other pilots in the system have said.
   *
   * Owned by the app rather than by either view, so moving between a berth and
   * the cockpit does not lose the conversation.
   */
  chat?: readonly ChatLine[];
  /** Say something. `/alert ...` spends a beacon and interrupts everybody. */
  onChat?: (channel: string, text: string) => void;
  /** This player's own uuid, for telling their lines from everyone else's. */
  uuid?: string;
  /**
   * Everybody else in the system.
   *
   * Docking is a camera and a set of panels, not a different world -- this view
   * already builds and animates the whole system, so the traffic outside belongs
   * in it exactly as it does in flight. Without this a berth was the one place
   * players could not see each other.
   */
  peers?: PeerFeed | null;
  seed: string;
  systemId: SystemId;
  /** Which berth. Chosen by whatever put us here — arriving, or starting. */
  stationId: string;
  padId: string;
  /** The blueprint. What the editor draws, and what a repair restores to. */
  design: ShipGraph;
  /** The hull in service, dents and all. Null before the first launch. */
  hull: ShipGraph | null;
  /** How much of it is left, 0..1. */
  integrity: number;
  /** Build a fresh hull to the blueprint. A repair, or a rebuy after a loss. */
  onRepair: () => void;
  wallet: Wallet;
  onWalletChange: (wallet: Wallet) => void;
  cargo: readonly CargoItem[];
  onCargoChange: (cargo: CargoItem[]) => void;
  /** Hold space, in weight. */
  capacity: number;
  /**
   * Something the app wants said on this view's radio.
   *
   * Pushed when its id changes, so the same notice arriving twice is announced
   * once. The app cannot reach the radio itself — each view keeps its own log —
   * so this is the way in.
   */
  notice?: Alert | null;
  onUndock: () => void;
  onOpenStarmap: () => void;
  onOpenShipBuilder: () => void;
  onOpenCargo: () => void;
  /** How much of this station's shelf everyone has taken this window. */
  shelfTaken?: Record<string, number>;
  /** Ask the server to do something. See `runAction`. */
  act: (path: string, body?: Record<string, unknown>) => Promise<boolean>;

  /**
   * A feature has been used, by name. See `lessons.ts`.
   *
   * Passed through to the panels rather than watched for here: a berth is a room
   * full of separate errands, and each of them knows the moment it becomes worth
   * explaining better than this does.
   */
  onFeatureUsed?: (lessonId: string) => void;

  /**
   * Which counters this account has unlocked. See `stationPanels.ts`.
   *
   * A new pilot lands with the mission board alone and the rest appear as they
   * become worth showing, which is the difference between arriving somewhere
   * and arriving in front of a wall of shop. Undefined means every counter —
   * the shape a caller that has not been told about this has, and the one that
   * cannot accidentally hide a service from somebody who has earned it.
   */
  unlocked?: readonly string[];

  /**
   * The tutorial step, if one is running.
   *
   * The introduction does not end when the gear touches the plate — the last
   * thing Control says is said to a pilot who has already landed, and before
   * this the berth was the one room in the game where it could not reach you.
   */
  tutorial?: TutorialStep | null;
  /** Who Control is addressing. The display id — see `TutorialContext`. */
  callsign?: string;
  /** Fired once the closing line has been delivered, so it is not replayed. */
  onTutorialDelivered?: () => void;
}

export function DockedView({
  peers,
  chat,
  onChat,
  uuid,
  seed,
  systemId,
  stationId,
  padId,
  design,
  hull,
  integrity,
  onRepair: _onRepair,
  wallet,
  onWalletChange,
  cargo,
  onCargoChange,
  capacity,
  notice = null,
  onUndock,
  onOpenStarmap,
  onOpenShipBuilder,
  onOpenCargo,
  shelfTaken = {},
  act,
  onFeatureUsed,
  unlocked,
  tutorial = null,
  callsign = 'pilot',
  onTutorialDelivered,
}: DockedViewProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  /*
   * The log and the interrupt queue, with the room's traffic already folded in.
   *
   * Shared with the cockpit — see `useRadio`. The log stays per view because it
   * also carries this station's own transmissions, which have no business
   * following the player out to the ship.
   */
  const {
    messages: radio,
    setMessages: setRadio,
    alerts,
    setAlerts,
  } = useRadio(chat, uuid);

  /*
   * Read by the render loop, not by React.
   *
   * The scene is built once and torn down on a system change; a feed arriving
   * afterwards must not rebuild it, and peers changing ten times a second must
   * not re-render anything at all.
   */
  const peersRef = useRef(peers);
  peersRef.current = peers;


  /*
   * Anything the app asked to have said, said once.
   *
   * Keyed on the id rather than the object, because the app rebuilds its state
   * on every action and an effect watching the notice itself would repeat the
   * same transmission on the next unrelated render.
   */
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (!notice || announced.current === notice.id) return;
    announced.current = notice.id;
    setAlerts((queue) => [...queue, notice]);
  }, [notice, setAlerts]);

  /** Which cargo row is open for selling. One at a time; see `CargoRow`. */
  const [selling, setSelling] = useState<string | null>(null);
  const [soldNote, setSoldNote] = useState<string | null>(null);

  /**
   * What this trader has on the shelf, which decides what they will pay.
   *
   * Sixty per cent for something they already stock, eighty for something they
   * do not — so the hold needs to know the shop, even though the selling is
   * done here.
   */
  const stock = useMemo(() => traderStock(seed, stationId), [seed, stationId]);

  const sell = (item: CargoItem, count: number) => {
    // Quoted here to show a figure; the server prices it again and pays what it
    // decides. What a trader already stocks is what decides the rate, and the
    // server reads its own shelf rather than trusting this one.
    const quote =
      sellPrice(item, stock.some((shelf) => shelf.id === item.id), { seed, stationId }) * count;

    void act('/trade/sell', { itemId: item.id, count }).then((ok) => {
      if (!ok) return;
      setSelling(null);
      setSoldNote(
        quote > 0
          ? `Sold ${count} × ${item.label} for ${formatLinelits(quote)}L.`
          : `Dumped ${count} × ${item.label}. Nobody here wants it.`,
      );
    });
  };


  /**
   * The station itself, for the panels.
   *
   * The scene has its own copy inside the effect; this is the one the React
   * tree needs, and resolving it twice is cheaper than threading it out of a
   * closure that rebuilds whenever the berth changes.
   */
  const station = useMemo(() => {
    const system = getSystem(seed, systemId);
    return system.stations.find((s) => s.id === stationId) ?? system.stations[0] ?? null;
  }, [seed, systemId, stationId]);

  /** The same voice as on the way in — seeded off the station, as in flight. */
  const controller = useMemo(
    () => makeOfficial(Rng.forEntity(seed, stationId, 'control'), CONTROL_POST),
    [seed, stationId],
  );

  /**
   * The closing line, once.
   *
   * `onTutorialDelivered` clears the step upstream, which is what keeps the
   * welcome from replaying every time you set down here for the rest of the
   * account's life. The alert itself lives in local state, so clearing the prop
   * does not take it back off the screen.
   */
  const delivered = useRef<TutorialStep | null>(null);

  useEffect(() => {
    /*
     * Two beats are said at a berth, not one.
     *
     * `deliver` is the arrival — take the job on the board — and `done` is the
     * sign-off after it has been handed in. Everything between them happens in
     * flight and is said by the cockpit.
     */
    if (tutorial !== 'done' && tutorial !== 'deliver') return;

    /*
     * Not once the job is aboard.
     *
     * `deliver` is "take the one job on our board", and it stays the current
     * step until the crate is handed in — so docking anywhere else while
     * carrying it replayed the welcome, word for word, at a station that had
     * nothing to offer. The line is about an empty hold, so an empty hold is
     * what it waits for.
     */
    if (tutorial === 'deliver' && cargo.some((item) => item.mission !== undefined)) return;

    // Latched *per beat* rather than once. Latching once was right while a
    // berth only ever said one thing; now the same view says the second one a
    // trip later, and a single flag would swallow it.
    if (delivered.current === tutorial) return;

    const beat = beatFor(tutorial, { callsign });
    if (!beat.line) return;

    // Set before the state updates, because this effect can run again before
    // they land — which would put the same line in the log twice.
    delivered.current = tutorial;

    const id = `control-${tutorial}`;
    setAlerts([{ id, from: controller.signature, source: station?.name, text: beat.line! }]);
    setRadio((log) => [
      ...log.slice(-60),
      { id, channel: 'station', from: controller.rank, text: beat.line! },
    ]);

    /*
     * Only the sign-off retires the introduction.
     *
     * `deliver` has to survive being said: it is what gates the mission board
     * down to one offer and what the rest of the run out advances from. Clearing
     * it here would take the job off the board in the same breath as telling the
     * player to go and take it.
     */
    if (tutorial === 'done') onTutorialDelivered?.();
  }, [tutorial, callsign, controller, station, cargo, onTutorialDelivered]);

  /**
   * Who is on shift, and what they say about it.
   *
   * The people are seeded off the station so they are the same every visit; the
   * lines are rolled here, on mount, so they are fresh every time you walk in.
   * Keyed on the station so arriving somewhere new re-rolls rather than
   * carrying the last berth's greetings across.
   */
  const crew = useMemo(() => {
    const roles: StaffRole[] = ['manager', 'trader', 'control', 'mechanic', 'painter', 'fabricator'];

    return new Map(
      roles.map((role) => [
        role,
        {
          who: crewmember(seed, stationId, role),
          says: crewLine(role, { float: Math.random }),
        },
      ]),
    );
  }, [seed, stationId]);

  /** The operator's colours. Everything docked is drawn in them. */
  const theme = useMemo(
    () => stationTheme((station?.allegiance as FactionId | null) ?? null),
    [station],
  );
  const [berth, setBerth] = useState({
    station: '',
    system: '',
    pad: '',
    allegiance: '',
    operator: '',
  });

  /*
   * F lifts off, the same key that sets down.
   *
   * One key for "leave the berth" and "take the berth" — you are never in a
   * position to mean both, and a berth you landed on with F should not need the
   * mouse to leave.
   *
   * Ignored while something on the panel has focus, or the key would type an F
   * into the sell box and undock at the same time.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.repeat) return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

      if (isAction('dock', event.key.toLowerCase())) onUndock();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onUndock]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const system = getSystem(seed, systemId);
    const station = system.stations.find((s) => s.id === stationId) ?? system.stations[0];
    if (!station) return;

    const geometry = getStationGeometry(seed, station);
    const pad = geometry.pads.find((p) => p.padId === padId) ?? geometry.pads[0];
    if (!pad) return;

    setBerth({
      station: station.name,
      system: system.name,
      pad: `${pad.padId} · class ${PAD_LABELS[pad.size]}`,
      allegiance: station.allegiance ?? 'unaligned',
      // The faction's actual name, never its id — "yellow" is a filename, not
      // something a player should ever be shown.
      operator: station.allegiance ? FACTIONS[station.allegiance].name : 'Independent',
    });

    const scene = new THREE.Scene();
    const background = createSpaceBackground();
    scene.add(background.mesh);

    const sky = createSystemSky(seed, systemId);
    scene.add(sky.points);

    scene.add(new THREE.AmbientLight(0xffffff, 0.16));
    scene.add(new THREE.PointLight(new THREE.Color(system.starColor), 2.4, 0, 0));

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const camera = new THREE.PerspectiveCamera(FOV, width / height, 0.5, 4_000_000);
    camera.up.set(0, 0, 1);

    const view = mountViewport(
      mount,
      (w, h) => {
        width = w;
        height = h;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      },
      { logarithmicDepthBuffer: true },
    );
    const renderer = view.renderer;

    // The whole system, running. Docking does not leave the world behind.
    const built = buildSystemScene(seed, systemId);
    scene.add(built.group);

    const deck = built.stationNodes.get(station.id);
    if (!deck) return;

    /*
     * Everyone else, in the same scene the station is in.
     *
     * No floating origin here -- this view draws in true coordinates, with the
     * camera parked on a deck rather than the world shifted under a ship -- so
     * the origin handed over is zero and absolute positions are used as they
     * arrive. That is the one thing to get right, and it is the reason this is
     * a lookup rather than a copy of the flight view's arithmetic.
     */
    const others = createRemoteShips(scene);

    /*
     * Nothing is shifted here, so the origin is zero.
     *
     * This view draws in true coordinates with the camera parked on a deck,
     * rather than moving the world under a ship the way the cockpit does. Same
     * lookups, one argument apart — see `sceneLookups`.
     */
    const HERE = new THREE.Vector3();
    const { padAt, anchorAt } = sceneLookups(built, HERE);

    const padExtent = pad.extent * GAME_SCALE;

    // ── The berth ────────────────────────────────────────────────────────────
    // Parented to the deck rather than placed in the scene, so it inherits the
    // station's orbit and spin for free — and cannot drift out of alignment
    // with the pad it belongs to, however the station moves.
    const anchor = new THREE.Object3D();
    // On the pad, not in it.
    //
    // The top of the plate, not the deck's top face: the plate is a slab laid
    // *on* that face, so anchoring to the deck put a ship half-buried in its
    // own berth. `padSurface` is the one place that stacks them, which is what
    // stops this drifting from the capture volume again.
    anchor.position.set(pad.position.x, pad.position.y, padSurface(geometry, pad));
    deck.add(anchor);

    // Your ship, if you have built one. Designs are authored in pad-local
    // coordinates, so it drops straight onto the anchor with no conversion —
    // and rides the station's orbit along with everything else parented here.
    // Cut open: almost everything a ship carries lives inside it and is
    // invisible in flight, which would make your own ship an opaque box in the
    // one place you come to look at it.
    // Drawn from the hull rather than the blueprint, so what is sitting on the
    // pad is the ship you actually own — a repair is something you can see.
    const parked = hull ?? design;
    const ship = parked.lines.length > 0 ? buildShipMesh(parked, { cutaway: true, landed: true }) : null;
    if (ship) {
      // The design is in world units and the deck is drawn in generator units,
      // so undo the station scale to put the ship at its true size on the pad.
      ship.group.scale.setScalar(1 / GAME_SCALE);
      // A ship is modelled centred on its own depth, so standing one on a
      // surface means lifting it by half of that — plus however far its landing
      // gear reaches below the hull, which is what it is really resting on.
      ship.group.position.set(0, 0, (SHIP_DEPTH / 2 + ship.standoff) / GAME_SCALE);
      anchor.add(ship.group);
    } else {
      // Nothing built yet: mark the reserved space rather than leaving a bare
      // pad, so the berth reads as yours and empty rather than as unfinished.
      const clearance = pad.extent * 0.55;
      const outline = new THREE.Mesh(
        withEdges(new THREE.BoxGeometry(pad.extent * 0.82, pad.extent * 0.82, clearance)),
        createStructureMaterial({ color: 0x2c4a63, fadeDistance: padExtent * 24, ambient: 0.85 }),
      );
      outline.position.set(0, 0, clearance / 2);
      anchor.add(outline);
    }

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    // Panning is allowed so you can look around your own ship, but the pivot is
    // kept over the pad. Docked is not a place you fly around — letting the
    // pivot wander onto the station or off into the orbit leaves you looking at
    // nothing with no obvious way back.
    controls.enablePan = true;
    controls.screenSpacePanning = true;

    // The same bindings as the starmap and the system view. This was on
    // OrbitControls' defaults, so the dock alone rotated on left-drag and
    // zoomed on middle — every view in the game handling the mouse differently
    // is the kind of thing you stop noticing and never stop paying for.
    controls.mouseButtons = { LEFT: null, MIDDLE: THREE.MOUSE.ROTATE, RIGHT: THREE.MOUSE.PAN };
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.zoomSpeed = 1.1;
    // Close enough to put your face against a component. The berth is the one
    // place you inspect your own ship in detail, so the limit is set by the
    // near plane rather than by the pad.
    controls.minDistance = padExtent * 0.08;
    controls.maxDistance = Math.max(padExtent * 14, geometry.radius * GAME_SCALE * 3);
    // Never below the deck — there is nothing under a station but its underside.
    controls.maxPolarAngle = Math.PI * 0.49;

    // Placed on the first frame, once the station has been put in its orbit.
    const berthPosition = new THREE.Vector3();
    built.animate(0);
    anchor.getWorldPosition(berthPosition);
    controls.target.copy(berthPosition);
    camera.position
      .copy(berthPosition)
      .add(new THREE.Vector3(padExtent * 1.5, -padExtent * 2.1, padExtent * 1.25));

    /**
     * Ride the berth around its orbit.
     *
     * The station is a moving platform, so the pivot has to be re-read from the
     * live node every frame and the camera carried by the same delta — moving
     * the pivot alone would swing the view around instead of holding still
     * relative to the deck you are parked on.
     */
    const previous = berthPosition.clone();
    const drift = new THREE.Vector3();
    const followBerth = () => {
      anchor.getWorldPosition(berthPosition);
      drift.subVectors(berthPosition, previous);
      previous.copy(berthPosition);

      controls.target.add(drift);
      camera.position.add(drift);
    };

    /**
     * Keep the pivot over the berth.
     *
     * Measured against the live pad position rather than a captured one, since
     * the station is in orbit and the pad is somewhere new every frame. The
     * camera is carried by the same correction, so a pan that runs into the
     * limit stops dead instead of swinging the view around.
     */
    const limit = padExtent / 2;
    const offset = new THREE.Vector3();
    const local = new THREE.Vector3();
    const confinePan = () => {
      offset.subVectors(controls.target, berthPosition);

      // Measured in the deck's own frame, because the station is in orbit and
      // rotating — a pad-relative box in world axes would twist as it went
      // round and let the pivot drift off the deck on one side.
      local.copy(offset);
      anchor.worldToLocal(local.add(berthPosition));

      // Flat on the pad. Panning slides you around the surface; height belongs
      // to zoom alone, so the view cannot end up under the deck or floating
      // above it with the ship edge-on.
      local.x = THREE.MathUtils.clamp(local.x, -limit / GAME_SCALE, limit / GAME_SCALE);
      local.y = THREE.MathUtils.clamp(local.y, -limit / GAME_SCALE, limit / GAME_SCALE);
      local.z = 0;

      anchor.localToWorld(local);
      const correction = local.sub(controls.target);
      if (correction.lengthSq() < 1e-12) return;

      controls.target.add(correction);
      camera.position.add(correction);
    };

    const blockMenu = (e: Event) => e.preventDefault();
    renderer.domElement.addEventListener('contextmenu', blockMenu);

    let frame = 0;

    const tick = () => {
      frame = requestAnimationFrame(tick);

      // Driven by the wall clock, not by how long this view has been open, so
      // leaving a system and coming back finds it where it should be rather
      // than rewound to the start. Slowed to flight pace — at map speed a
      // station drifts out from under you while you line up on its pad.
      built.animate(flightTime());
      // After the station has moved, so a ship parked on a pad is placed against
      // where that pad is now rather than where it was a frame ago.
      others.update(peersRef.current ?? null, HERE, peerClock(), padAt, anchorAt);
      followBerth();
      controls.update();
      confinePan();

      background.follow(camera.position);
      sky.follow(camera.position);
      renderer.render(scene, camera);
    };
    tick();

    return () => {
      cancelAnimationFrame(frame);
      others.dispose();
      renderer.domElement.removeEventListener('contextmenu', blockMenu);
      sky.dispose();
      controls.dispose();
      background.dispose();
      ship?.dispose();
      built.dispose();
      view.dispose();
    };
  }, [seed, systemId, stationId, padId, design]);

  /*
   * The counters this berth is actually showing.
   *
   * Filtered through `STATION_PANELS` rather than used as given, so the order is
   * the order a berth always draws them in: a service that opens later appears
   * where it will always be rather than arriving on the end and moving once
   * something else unlocks.
   */
  /*
   * Empty means "not told", not "nothing open".
   *
   * The board is always open — `OPENING_PANELS` — so a real answer can never be
   * empty, and an empty one is a cached record written before this existed or a
   * server that has not been told about it. Reading that as a berth with no
   * counters in it would be the one failure worth avoiding, so it reads as a
   * berth with all of them.
   */
  const known = unlocked && unlocked.length > 0 ? unlocked : null;
  const shown = STATION_PANELS.filter((panel) => !known || known.includes(panel));
  const open = (panel: StationPanel) => shown.includes(panel);
  // Whichever is rightmost gets no divider, which is not always the mechanic.
  const rightmost = shown[shown.length - 1];

  return (
    <>
      <div ref={mountRef} style={{ position: 'absolute', inset: 0 }} />

      {/* One console, across the width of the berth.
          No tabs: everything a station does is on screen together, because
          docking is the part of the game where you *compare* things — what a
          repair costs against what the trader wants against what will fit in
          the hold. Hiding four of those behind the fifth turned every decision
          into a memory test.

          Coloured by whoever runs the station, so arriving somewhere new looks
          like arriving somewhere new. See `stationTheme`. */}
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          /*
           * As wide as it has counters, and never wider than the room it has.
           *
           * This was `right: 300` — pinned to the radio's edge — which was right
           * while every berth had five columns and wrong the moment they could
           * be hidden. A new pilot with the mission board alone got one column
           * stretched across two thirds of the screen, which reads as a broken
           * layout rather than as a station with one counter open.
           *
           * The cap is what the old rule computed: the screen, less this
           * margin, less the radio's gutter.
           */
          /*
           * Room for the counters, in the panel's own units.
           *
           * Both halves have to account for the HUD scale. The gutter is there
           * to clear the radio, which is drawn smaller on a phone and needs
           * proportionally less; and the available width is divided by the scale
           * because everything here is measured *before* the transform shrinks
           * it. Left in physical units, a phone computed `100% - 316px` — about
           * fifty points — and drew a station panel narrower than one button.
           */
          width: `min(calc((100% - ${16 + 300 * HUD_SCALE}px) / ${HUD_SCALE}), ${
            Math.max(1, shown.length) * COLUMN
          }px)`,
          /*
           * A third of the screen, and then some.
           *
           * The original third was chosen so the berth you are standing on is
           * not buried behind a wall of shop — that still holds, and full height
           * would still be wrong. But a third was tight in practice: a mission
           * list or a trader's stock spends most of it scrolled.
           *
           * A tenth more on a desktop. Half again on a phone, where it is worth
           * more: the panel is *drawn* at 0.65, so a third of the viewport in
           * these units is barely a fifth of the screen, and the scroll it
           * forced was the fiddliest thing in the berth. Multiplying the height
           * multiplies what is rendered by the same factor, so these read as
           * what they say.
           */
          height: TOUCH_UI ? `${BERTH_HEIGHT * 1.5}vh` : `${BERTH_HEIGHT * 1.1}vh`,
          display: 'flex',
          flexDirection: 'column',
          background: theme.backing,
          border: `1px solid ${theme.edge}`,
          color: theme.text,
          ...hudScaled('top left'),
        }}
      >
        {/* One column per open counter. Chat is a service too and lives in the
            radio on the right, where it already is in flight. */}
        <ScrollFades theme={theme}>
        <div
          style={{
            flex: 1,
            display: 'grid',
            /*
             * A counter is `COLUMN` wide or it is not a counter.
             *
             * This was `1fr` per open service, which shares out whatever room
             * there is — right on a desktop, where there is more than enough,
             * and wrong on a phone, where five counters divided a screen into
             * five slivers and every one of them was unusable. A mission with a
             * three-word name wrapped to four lines.
             *
             * Fixed width and a scroll instead: as many counters as the screen
             * can actually hold, each the size it was designed at, and the rest
             * a swipe away. The panel's own width is already capped at what fits
             * (see above), so on a desktop this is exactly the layout it had —
             * the columns simply reach the cap rather than being squeezed under
             * it.
             */
            gridTemplateColumns: `repeat(${Math.max(1, shown.length)}, ${COLUMN}px)`,
            /*
             * `auto`, and the two behaviours it needs.
             *
             * `overscroll-behavior` so a swipe that runs out of counters does not
             * hand the gesture to the page behind — which on a phone is the
             * whole berth. And both pan axes named explicitly rather than left
             * to `auto`: horizontal is this row, vertical belongs to the list
             * inside whichever column the finger is on, and neither is a zoom.
             */
            overflowX: 'auto',
            overscrollBehaviorX: 'contain',
            touchAction: 'pan-x pan-y',
            minHeight: 0,
          }}
        >
          {open('missions') && (
          <Section
            theme={theme}
            glyph="◈"
            label="missions"
            speaker={crew.get('manager')}
            last={rightmost === 'missions'}
          >
            <MissionBoard
              seed={seed}
              systemId={systemId}
              stationId={stationId}
              theme={theme}
              cargo={cargo}
              onCargoChange={onCargoChange}
              wallet={wallet}
              onWalletChange={onWalletChange}
              capacity={capacity}
              act={act}
              // The whole introduction, not the one step: a player who undocked
              // without taking the job must still find it there.
              introducing={tutorial !== null && tutorial !== 'done' && tutorial !== 'abandoned'}
              onFeatureUsed={onFeatureUsed}
            />
          </Section>
          )}

          {open('trader') && (
          <Section
            theme={theme}
            glyph="⇄"
            label="trader"
            last={rightmost === 'trader'}
            speaker={crew.get('trader')}
            readout={
              <>
                {/* Split the way it is actually held: what is on the ship is
                    what you can spend and what you can lose. */}
                <Readout
                  theme={theme}
                  label="ship"
                  value={`${formatLinelits(wallet.carried)}L`}
                  warn={wallet.carried > 0}
                />
                <Readout theme={theme} label="vault" value={`${formatLinelits(wallet.banked)}L`} />
              </>
            }
          >
            <StationShop
              seed={seed}
              stationId={stationId}
              hull={hull ?? design}
              wallet={wallet}
              onWalletChange={onWalletChange}
              cargo={cargo}
              onCargoChange={onCargoChange}
              capacity={capacity}
              taken={shelfTaken}
              act={act}
              onFeatureUsed={onFeatureUsed}
            />
          </Section>
          )}

          {open('reputation') && (
          <Section
            theme={theme}
            glyph="❖"
            label="reputation"
            speaker={crew.get('control')}
            last={rightmost === 'reputation'}
          >
            {/* Standing is not stored yet, so everyone reads at the starting
                value. The board is real; the numbers behind it are not. */}
            <ReputationBoard standing={{}} host={berth.allegiance as FactionId} />
          </Section>
          )}

          {open('cargo') && (
          <Section
            theme={theme}
            glyph="▤"
            label="cargo"
            last={rightmost === 'cargo'}
            readout={
              <Readout
                theme={theme}
                label="hold"
                value={`${cargoWeight(cargo).toFixed(1)}/${capacity}w`}
                over={cargoWeight(cargo) > capacity}
              />
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div style={{ height: 5, background: 'rgba(255,255,255,0.09)', position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    right: `${Math.max(0, 1 - cargoWeight(cargo) / Math.max(1, capacity)) * 100}%`,
                    background: cargoWeight(cargo) > capacity ? '#ff5f6b' : theme.accent,
                  }}
                />
              </div>

              {capacity === 0 && (
                <div style={{ fontSize: 11, color: '#ffb347' }}>
                  This hull has no vault, so it cannot carry anything. Fit one in the ship builder.
                </div>
              )}

              {soldNote && <div style={{ fontSize: 11, color: '#ffb347' }}>{soldNote}</div>}

              {cargo.length === 0 && capacity > 0 && (
                <div style={{ fontSize: 11, color: theme.dim }}>
                  Nothing aboard. The trader is along this row.
                </div>
              )}

              {/* Selling lives with the cargo rather than with the shop.
                  It is a thing you do *to your hold* — you open it to see what
                  is in there and decide what to be rid of — and putting it in
                  the trader's column meant looking at one list to decide about
                  another. */}
              {cargo.map((item) => (
                <CargoRow
                  key={item.id}
                  theme={theme}
                  item={item}
                  stocked={stock.some((shelf) => shelf.id === item.id)}
                  selected={selling === item.id}
                  onSelect={() => setSelling(selling === item.id ? null : item.id)}
                  onSell={(count) => sell(item, count)}
                  where={{ seed, stationId }}
                />
              ))}
            </div>
          </Section>
          )}

          {open('mechanic') && (
          <Section
            theme={theme}
            glyph="⚙"
            label="mechanic"
            speaker={crew.get('mechanic')}
            readout={
              <Readout
                theme={theme}
                label="hull"
                value={`${Math.round(integrity * 100)}%`}
                over={integrity < 0.4}
                warn={integrity < 1}
              />
            }
            last={rightmost === 'mechanic'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {/* Hull condition is in this panel's header — see `Readout`. It
                  was here as well, as a second bar saying the same thing. */}

              {/* The two specialists, who share the mechanic's shop rather than
                  getting a column of their own. A name only when the facility
                  is actually fitted — a station without a painter does not have
                  a painter standing in it being quiet. */}
              <Facility
                theme={theme}
                label="fabricator"
                fitted={station?.hasAdvancedFabricator ?? false}
                speaker={crew.get('fabricator')}
              />

              {/* The machine itself, under the person who runs it.
                  Only where there is one — a station without a fabricator shows
                  the facility as unfitted and nothing beneath it, rather than a
                  disabled panel explaining what it would have done. */}
              {station?.hasAdvancedFabricator && (
                <FabricatorPanel wallet={wallet} act={act} />
              )}
              <Facility
                theme={theme}
                label="painter"
                fitted={station?.hasPainter ?? false}
                speaker={crew.get('painter')}
              />

              <button
                type="button"
                onClick={onOpenShipBuilder}
                style={{
                  ...actionStyle,
                  background: theme.active,
                  borderColor: theme.accent,
                  color: theme.accent,
                }}
              >
                open ship builder
              </button>
            </div>
          </Section>
          )}
        </div>
        </ScrollFades>
      </div>

      {/* The radio, where it is in flight. Docking should not cost you a
          conversation, and moving it would mean learning two places. */}
      <FlightRadio
        messages={radio}
        onSend={(channel, text) => {
          // No local echo when there is a room: it broadcasts to the sender too,
          // so echoing here would print every line twice.
          if (onChat) {
            onChat(channel, text);
            return;
          }
          setRadio((log) => [
            ...log.slice(-60),
            { id: `${Date.now()}`, channel, from: 'you', text, own: true },
          ]);
        }}
      />

      {/* Control, still talking. Same panel as in flight, moved to the bottom
          because the station console takes the top third of the berth and a
          hail up there would be talking over it. */}
      <RadioAlert
        placement="bottom"
        alerts={alerts}
        onDone={(id) => setAlerts((queue) => queue.filter((alert) => alert.id !== id))}
      />

      {/* The two ways out. The starmap used to be here *and* in the corner
          switcher; one of them had to go, and this is the one that was
          duplicated.

          `stretch` is what levels the row, and it only works because neither
          button states a height of its own — see below. */}
      <div style={{ position: 'absolute', bottom: 16, left: 16, display: 'flex', gap: 10, alignItems: 'stretch' }}>
        {/* Whose dock this is. Back beside the controls that leave it, which is
            where you look when you are deciding whether to. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 14px',
            background: theme.backing,
            border: `1px solid ${theme.edge}`,
            color: theme.text,
          }}
        >
          {berth.allegiance !== 'unaligned' && (
            <img src={factionIconUrl(berth.allegiance as FactionId)} alt="" width={28} height={28} />
          )}
          <div>
            <div style={{ fontSize: 16, lineHeight: 1.2, color: theme.accent }}>{berth.station}</div>
            <div style={{ fontSize: 10.5, color: theme.dim }}>
              {theme.abbreviation ? `${theme.abbreviation} · ` : ''}
              {berth.operator}
            </div>
            <div style={{ fontSize: 10.5, color: theme.dim }}>
              {berth.system} · {berth.pad}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onUndock}
          /*
           * No height, on purpose.
           *
           * The row is `alignItems: 'stretch'`, so a button with no height of
           * its own comes out exactly as tall as the tallest thing in it — which
           * is the station card, and the card is sized by its contents: a name,
           * an operator and a system line. A fixed 44 overrode the stretch and
           * sat a good fifteen pixels short of it, so the two ways out looked
           * like they belonged to a different row than the berth they leave.
           *
           * Vertical padding is zero for the same reason: the stretch decides
           * the height, and padding on top of it would only fight the card.
           */
          style={{
            ...actionStyle,
            padding: '0 22px',
            background: theme.active,
            borderColor: theme.accent,
            color: theme.accent,
            fontSize: 13,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
          }}
          title="Leave the berth (F)"
        >
          undock · F
        </button>
        <button
          type="button"
          onClick={onOpenStarmap}
          // Height left to the stretch, as above.
          style={{
            ...actionStyle,
            padding: '0 18px',
            background: theme.backing,
            borderColor: theme.edge,
            color: theme.text,
          }}
        >
          ✧ starmap
        </button>
      </div>
    </>
  );
}

/**
 * One column of the station console.
 *
 * Scrolls on its own, so a long trader shelf cannot push the mechanic off the
 * bottom of the screen — five columns that share a scrollbar are five columns
 * you have to scroll past to read one.
 */
/**
 * A specialist who works out of the mechanic's shop.
 *
 * Same shape as the fitted/none line it replaces, with the person folded in
 * underneath when there is one. Kept as a row rather than promoted to its own
 * column because that is what they are: a service the yard either has or does
 * not, not a sixth place to go.
 */
/**
 * One line of the hold, and what it takes to sell it.
 *
 * Collapsed it is a name, a count and a weight. Opened it grows a spinner and a
 * total — the amount is chosen deliberately and the price shown before the
 * button, rather than a row that empties itself on a mis-click. There is no
 * undo, so the commitment has to be the second click rather than the first.
 */
function CargoRow({
  theme,
  item,
  stocked,
  selected,
  onSelect,
  onSell,
  where,
}: {
  theme: StationTheme;
  item: CargoItem;
  /** Whether this trader already has one, which is what sets the price. */
  stocked: boolean;
  selected: boolean;
  onSelect: () => void;
  onSell: (count: number) => void;
  /** Which counter is asking. Trinkets are priced by the station, not the item. */
  where: { seed: string; stationId: string };
}) {
  const [count, setCount] = useState(1);
  const each = sellPrice(item, stocked, where);

  /*
   * What this lot cost, and therefore whether selling it here is a win.
   *
   * Only trinkets carry a price paid, and only trinkets need one: everything
   * else in the hold is worth what it is made of, so there is no such thing as a
   * good or a bad place to sell a reactor. This is the entire readout the trade
   * runs on — without it a player is comparing a number on screen against one
   * they wrote down three systems ago.
   */
  const paid = item.kind === 'trinket' ? (item.paid ?? 0) : null;
  const margin = paid === null ? null : each - paid;

  // Reset whenever the row closes, so reopening it does not remember a number
  // from a stack that may since have changed size.
  useEffect(() => {
    if (!selected) setCount(1);
  }, [selected]);

  const wanted = Math.max(1, Math.min(count, item.count));

  return (
    <div style={{ borderBottom: selected ? `1px solid ${theme.edge}` : undefined, paddingBottom: selected ? 6 : 0 }}>
      <button
        type="button"
        onClick={onSelect}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          padding: 0,
          background: 'transparent',
          border: 'none',
          color: selected ? theme.accent : 'inherit',
          font: 'inherit',
          fontSize: 11.5,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.label}
        </span>
        <span style={{ color: theme.dim, width: 26, textAlign: 'right' }}>×{item.count}</span>
        <span
          style={{
            color: theme.dim,
            width: 48,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {itemWeight(item).toFixed(2)}w
        </span>
      </button>

      {/* What this lot cost and what it fetches here, on the row rather than
          behind a click — deciding whether to sell is the *whole* mechanic, and
          it cannot be done from a number you have to open a menu to see.

          Coloured by the margin because that is the one question being asked. */}
      {paid !== null && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 10.5,
            color: theme.dim,
            paddingLeft: 2,
            marginTop: 1,
          }}
        >
          <span>paid {paid} each</span>
          <span style={{ color: margin! > 0 ? '#7fb46f' : margin! < 0 ? '#ff5f6b' : theme.dim }}>
            {each} here {margin! > 0 ? `(+${margin})` : margin! < 0 ? `(${margin})` : '(even)'}
          </span>
        </div>
      )}

      {selected && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5 }}>
          <button type="button" onClick={() => setCount(Math.max(1, wanted - 1))} style={step(theme)}>
            −
          </button>
          <span style={{ fontSize: 11.5, width: 22, textAlign: 'center' }}>{wanted}</span>
          <button
            type="button"
            onClick={() => setCount(Math.min(item.count, wanted + 1))}
            style={step(theme)}
          >
            +
          </button>
          <button type="button" onClick={() => setCount(item.count)} style={step(theme)}>
            all
          </button>

          <button
            type="button"
            onClick={() => onSell(wanted)}
            style={{
              flex: 1,
              padding: '3px 8px',
              background: `${theme.accent}1c`,
              border: `1px solid ${theme.accent}66`,
              color: theme.accent,
              font: 'inherit',
              fontSize: 11,
              cursor: 'pointer',
            }}
            title={
              item.mission
                ? 'A delivery. Worth nothing to a trader — hand it in at the mission board.'
                : stocked
                  ? 'This trader already stocks it, so it fetches less here'
                  : 'This trader has none, so it fetches more here'
            }
          >
            {each > 0 ? `sell for ${formatLinelits(each * wanted)}L` : 'dump — no value'}
          </button>
        </div>
      )}
    </div>
  );
}

function step(theme: StationTheme): React.CSSProperties {
  return {
    width: 22,
    padding: '2px 0',
    background: 'transparent',
    border: `1px solid ${theme.edge}`,
    color: theme.dim,
    font: 'inherit',
    fontSize: 11,
    cursor: 'pointer',
  };
}

function Facility({
  theme,
  label,
  fitted,
  speaker,
}: {
  theme: StationTheme;
  label: string;
  fitted: boolean;
  speaker?: { who: Crewmember; says: string };
}) {
  // Not fitted: the yard simply does not offer it, and there is nobody to name.
  if (!fitted || !speaker) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
        <span style={{ color: theme.dim, fontSize: 9.5, letterSpacing: 0.8 }}>
          {label.toUpperCase()}
        </span>
        <span style={{ color: theme.dim }}>none</span>
      </div>
    );
  }

  // Fitted: billed exactly like the people who have a column of their own. They
  // do not get their own panel, but they are not a line item either.
  return <Speaker theme={theme} speaker={speaker} compact />;
}

/** Header numbers sit in the panel text colour, not the accent. */
const HEADER_READOUT = 'rgba(226,238,255,0.9)';

/**
 * One figure in a panel header.
 *
 * Label first and small, value second and legible — the same shape across all
 * three so purse, hold and hull scan as one row of instruments rather than
 * three panels that each invented their own readout.
 */
function Readout({
  theme,
  label,
  value,
  warn = false,
  over = false,
}: {
  theme: StationTheme;
  label: string;
  value: string;
  /** Worth noticing. Carried linelits, a dented hull. */
  warn?: boolean;
  /** Actually a problem. Over capacity, badly damaged. */
  over?: boolean;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
      <span style={{ color: theme.dim, fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ color: over ? '#ff5f6b' : warn ? '#ffb347' : HEADER_READOUT }}>{value}</span>
    </span>
  );
}

/**
 * Somebody on shift, with what they said on the way in.
 *
 * Shared so a specialist tucked into the mechanic's shop is introduced the same
 * way as the trader with a column to themselves — name in the station's accent,
 * post in small capitals after it, line quiet and italic underneath.
 */
function Speaker({
  theme,
  speaker,
  compact = false,
}: {
  theme: StationTheme;
  speaker: { who: Crewmember; says: string };
  compact?: boolean;
}) {
  return (
    <div style={{ marginBottom: compact ? 0 : 9 }}>
      <div style={{ fontSize: compact ? 10.5 : 11, color: theme.accent }}>
        {speaker.who.name}
        <span style={{ color: theme.dim, marginLeft: 6, fontSize: 9.5, letterSpacing: 0.8 }}>
          {speaker.who.title.toUpperCase()}
        </span>
      </div>
      <div
        style={{
          fontSize: compact ? 10.5 : 11,
          color: theme.dim,
          fontStyle: 'italic',
          lineHeight: 1.45,
          marginTop: 3,
        }}
      >
        “{speaker.says}”
      </div>
    </div>
  );
}

/**
 * Says which way there is more, when there is more.
 *
 * The counters scroll sideways on a narrow screen, and a row that scrolls with
 * no sign that it does is a row where two of the five services do not exist as
 * far as the player is concerned. There is no scrollbar to go by either — a
 * touch device draws none until you are already scrolling, which is the wrong
 * order to learn it in.
 *
 * So the edge with more behind it fades out. Only that edge, and only while it
 * has something to say: a fade that is always on both sides is decoration, and
 * stops carrying information the moment it stops changing.
 *
 * Touch only. A desktop has the width to hold every open counter, a scrollbar
 * if it somehow does not, and a wheel that works either way.
 */
function ScrollFades({ theme, children }: { theme: StationTheme; children: React.ReactNode }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    if (!TOUCH_UI) return;

    // The scrolling element is the child. This wrapper exists only to be a
    // positioning context for the two overlays, so it must not scroll itself.
    const row = scroller.current?.firstElementChild;
    if (!(row instanceof HTMLElement)) return;

    const measure = () => {
      /*
       * A pixel of slack at each end.
       *
       * Sub-pixel layout means `scrollLeft` rarely lands exactly on zero or
       * exactly on the maximum — and this row is inside a 0.65 transform, which
       * guarantees fractions. Without the slack a fade never quite switches off
       * at either end, which is the one state that makes it meaningless.
       */
      setEdges({
        left: row.scrollLeft > 1,
        right: row.scrollLeft + row.clientWidth < row.scrollWidth - 1,
      });
    };

    measure();
    row.addEventListener('scroll', measure, { passive: true });

    // Counters open and close as the game is played, and the panel is resized by
    // rotating the phone. Both change whether there is anywhere to scroll to.
    const observer = new ResizeObserver(measure);
    observer.observe(row);

    return () => {
      row.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, []);

  if (!TOUCH_UI) return <>{children}</>;

  return (
    <div ref={scroller} style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      {children}

      {(['left', 'right'] as const).map((side) => (
        <div
          key={side}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            [side]: 0,
            width: 22,
            // Never in the way of a tap on the counter underneath.
            pointerEvents: 'none',
            /*
             * Half strength at the opaque end.
             *
             * The accent is the station's brightest colour and at full opacity it
             * reads as a border — a hard edge that looks like the panel ends
             * there, which is the opposite of what a fade is for. Half is still
             * unmistakably a fade and no longer competes with the counters.
             *
             * On the whole overlay rather than in the gradient's own colour, so
             * there is one number to turn and it does not have to be threaded
             * through every theme's accent as an alpha.
             */
            opacity: edges[side] ? 0.5 : 0,
            transition: 'opacity 160ms linear',
            /*
             * Opaque at the edge, clear inward — and the colour order is what
             * says so.
             *
             * `to left` points the gradient line leftward, which puts the *first*
             * stop at the right-hand end. Naming the backing first therefore
             * painted each fade on its inside edge and left the screen edge
             * clear: both of them mirrored, and a fade that darkens away from
             * the edge reads as a shadow rather than as more to come.
             */
            background: `linear-gradient(to ${side}, transparent, ${theme.accent})`,
          }}
        />
      ))}
    </div>
  );
}

function Section({
  theme,
  glyph,
  label,
  speaker,
  readout,
  last = false,
  children,
}: {
  theme: StationTheme;
  glyph: string;
  label: string;
  /** Whoever runs this service, and what they said on the way in. */
  speaker?: { who: Crewmember; says: string };
  /**
   * The one number this panel is about, in its header.
   *
   * Purse, hold, hull condition — each of them was a labelled row at the top of
   * its own column, which put the thing you came to check *below* the person
   * greeting you and cost a line of the panel to say what the header already
   * said. In the header it is legible at a glance across all five columns.
   */
  readout?: React.ReactNode;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRight: last ? 'none' : `1px solid ${theme.edge}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          borderBottom: `1px solid ${theme.edge}`,
          color: theme.accent,
          fontSize: 10,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>{glyph}</span>
        {label}

        {readout && (
          <span
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              color: HEADER_READOUT,
              letterSpacing: 0,
              textTransform: 'none',
              fontSize: 12,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {readout}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px' }}>
        {/* The person, above their service. Name in the station's accent so it
            reads as staff rather than as a heading, and the line quiet and
            italic so it never competes with the prices underneath it. */}
        {speaker && <Speaker theme={theme} speaker={speaker} />}

        {children}
      </div>
    </div>
  );
}


const topBar: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'stretch',
  gap: 12,
  padding: 10,
  background: 'linear-gradient(rgba(6,10,18,0.9), rgba(6,10,18,0))',
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  background: active ? 'rgba(127,212,255,0.18)' : 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  borderRight: 'none',
  color: active ? '#dbeaff' : 'rgba(219,234,255,0.7)',
  font: 'inherit',
  fontSize: 12,
  letterSpacing: 0.6,
  padding: '7px 18px',
  cursor: 'pointer',
});

const actionStyle: React.CSSProperties = {
  background: 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 12,
  padding: '7px 14px',
  cursor: 'pointer',
};

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  top: 122,
  left: 16,
  width: 320,
  background: 'rgba(8,14,24,0.9)',
  border: '1px solid rgba(127,212,255,0.22)',
  padding: '10px 14px',
  fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  padding: '10px 14px',
  minWidth: 170,
};
